import { useCallback, useEffect, useMemo, useRef } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  useCarts,
  useCreateCart,
  useRenameCart,
  useArchiveCart,
  useDeleteCart,
  useAddProductToCart,
  useAddProductsToCart,
  useRemoveProductFromCart,
} from './useCarts';
import { useToast } from '../context/ToastContext';
import { ApiError } from '../lib/api';

interface ActiveCartState {
  activeCartId: number | null;
  setActiveCartId: (id: number | null) => void;
}

export const useActiveCartStore = create<ActiveCartState>()(
  persist(
    (set) => ({
      activeCartId: null,
      setActiveCartId: (id) => set({ activeCartId: id }),
    }),
    {
      name: 'vivatis-active-cart-id',
      version: 1,
      migrate: (persisted, version) => {
        if (version === 0 && (typeof persisted === 'number' || persisted === null)) {
          return { activeCartId: persisted };
        }
        return persisted as ActiveCartState;
      },
    },
  ),
);

/**
 * Create an initial document for a newly registered user if they have none.
 *
 * MUST be rendered from a single, always-mounted place (Layout). Calling it
 * from multiple mounted components would race: each instance would observe
 * `carts.length === 0` before any of them finished creating and would fire
 * its own `createCart` mutation, ending up with several duplicate docs on
 * the very first login (VIVATISPDB-258). A module-level lock on top of the
 * per-instance ref makes this safe even if the hook is unmounted and
 * remounted mid-bootstrap (e.g. StrictMode, route changes).
 */
let bootstrapInFlight = false;

export function useBootstrapInitialCart(isLoggedIn: boolean): void {
  const { data: carts = [], isFetched } = useCarts(isLoggedIn);
  const { setActiveCartId } = useActiveCartStore();
  const createCartMutation = useCreateCart();
  const hasBootstrappedRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn) {
      hasBootstrappedRef.current = false;
      bootstrapInFlight = false;
      return;
    }
    if (
      isFetched &&
      carts.length === 0 &&
      !hasBootstrappedRef.current &&
      !bootstrapInFlight &&
      !createCartMutation.isPending
    ) {
      hasBootstrappedRef.current = true;
      bootstrapInFlight = true;
      createCartMutation.mutate('Document 1', {
        onSuccess: (created) => {
          if (created) setActiveCartId(created.id);
        },
        onSettled: () => {
          bootstrapInFlight = false;
        },
      });
    }
  }, [isLoggedIn, isFetched, carts.length, createCartMutation, setActiveCartId]);
}

export function useCartActions(isLoggedIn: boolean) {
  const { data: apiCarts = [], isFetched } = useCarts(isLoggedIn);
  const { activeCartId, setActiveCartId } = useActiveCartStore();
  const toast = useToast();

  // A locked document answers every write with 422 and a translated reason.
  // Without this the user would only ever see the generic English fallback
  // and could not tell a lock apart from a network hiccup (VIVATISPDB-402).
  const showWriteError = useCallback((error: unknown, fallback: string) => {
    const isLocked = error instanceof ApiError && error.status === 422;
    toast.show(isLocked ? error.message : fallback, 'error');
  }, [toast]);

  const archiveCartMutation = useArchiveCart();
  const createCartMutation = useCreateCart();
  const renameCartMutation = useRenameCart();
  const deleteCartMutation = useDeleteCart();
  const addProductMutation = useAddProductToCart();
  const addProductsMutation = useAddProductsToCart();
  const removeProductMutation = useRemoveProductFromCart();

  const carts = apiCarts;

  useEffect(() => {
    if (isFetched && activeCartId != null && carts.length > 0 && !carts.some((c) => c.id === activeCartId)) {
      setActiveCartId(carts[0]?.id ?? null);
    }
  }, [isFetched, activeCartId, carts, setActiveCartId]);

  const resolvedActiveCartId =
    activeCartId != null && carts.some((c) => c.id === activeCartId)
      ? activeCartId
      : carts[0]?.id ?? null;
  const activeCart = carts.find((c) => c.id === resolvedActiveCartId) ?? carts[0];
  const cart = useMemo(() => (activeCart?.products ?? []).map((p) => p.id), [activeCart]);

  const switchCart = useCallback((cartId: number) => {
    setActiveCartId(cartId);
  }, [setActiveCartId]);

  const createCart = useCallback((name: string, productIds: number[] = []) => {
    createCartMutation.mutate(name, {
      // Offline the mutation resolves without a server cart — the optimistic
      // row carrying the tempId is the only thing in the cache, so activate
      // that one instead of leaving the user in the previous document.
      onSuccess: (newCart, _name, context) => {
        const cartId = newCart?.id ?? (context?.tempId as unknown as number | undefined);
        if (cartId == null) return;
        setActiveCartId(cartId);
        if (productIds.length > 0) {
          addProductsMutation.mutate({ cartId, productIds }, {
            onError: (err) => showWriteError(err, 'Could not add products to cart'),
          });
        }
      },
      onError: (err) => showWriteError(err, 'Could not create cart'),
    });
  }, [createCartMutation, addProductsMutation, setActiveCartId, showWriteError]);

  const archiveCart = useCallback((cartId: number) => {
    archiveCartMutation.mutate(cartId, {
      onSuccess: () => {
        if (activeCartId === cartId) {
          const remaining = carts.filter((c) => c.id !== cartId);
          setActiveCartId(remaining[0]?.id ?? null);
        }
      },
      onError: (err) => showWriteError(err, 'Could not archive cart'),
    });
  }, [archiveCartMutation, activeCartId, carts, setActiveCartId, showWriteError]);

  const deleteCart = useCallback((cartId: number) => {
    deleteCartMutation.mutate(cartId, {
      onSuccess: () => {
        if (activeCartId === cartId) {
          const remaining = carts.filter((c) => c.id !== cartId);
          setActiveCartId(remaining[0]?.id ?? null);
        }
      },
      onError: (err) => showWriteError(err, 'Could not delete cart'),
    });
  }, [deleteCartMutation, activeCartId, carts, setActiveCartId, showWriteError]);

  const renameCart = useCallback((cartId: number, name: string) => {
    renameCartMutation.mutate({ cartId, name }, {
      onError: (err) => showWriteError(err, 'Could not rename cart'),
    });
  }, [renameCartMutation, showWriteError]);

  const addToCart = useCallback((productId: number) => {
    if (!resolvedActiveCartId) return;
    addProductMutation.mutate({ cartId: resolvedActiveCartId, productId }, {
      onError: (err) => showWriteError(err, 'Could not add product to cart'),
    });
  }, [resolvedActiveCartId, addProductMutation, showWriteError]);

  const addAllToCart = useCallback((productIds: number[]) => {
    if (!resolvedActiveCartId || productIds.length === 0) return;
    addProductsMutation.mutate({ cartId: resolvedActiveCartId, productIds }, {
      onError: (err) => showWriteError(err, 'Could not add products to cart'),
    });
  }, [resolvedActiveCartId, addProductsMutation, showWriteError]);

  const removeFromCart = useCallback((productId: number) => {
    if (!resolvedActiveCartId) return;
    removeProductMutation.mutate({ cartId: resolvedActiveCartId, productId }, {
      onError: (err) => showWriteError(err, 'Could not remove product from cart'),
    });
  }, [resolvedActiveCartId, removeProductMutation, showWriteError]);

  const clearCart = useCallback(() => {
    if (!resolvedActiveCartId) return;
    cart.forEach((productId) => {
      removeProductMutation.mutate({ cartId: resolvedActiveCartId, productId });
    });
  }, [resolvedActiveCartId, cart, removeProductMutation]);

  const isInCart = useCallback(
    (productId: number) => cart.includes(productId),
    [cart],
  );

  const addToSpecificCart = useCallback((cartId: number, productId: number) => {
    addProductMutation.mutate({ cartId, productId }, {
      onError: (err) => showWriteError(err, 'Could not add product to cart'),
    });
  }, [addProductMutation, showWriteError]);

  const removeFromSpecificCart = useCallback((cartId: number, productId: number) => {
    removeProductMutation.mutate({ cartId, productId }, {
      onError: (err) => showWriteError(err, 'Could not remove product from cart'),
    });
  }, [removeProductMutation, showWriteError]);

  const addAllToSpecificCart = useCallback((cartId: number, productIds: number[]) => {
    if (productIds.length === 0) return;
    addProductsMutation.mutate({ cartId, productIds }, {
      onError: (err) => showWriteError(err, 'Could not add products to cart'),
    });
  }, [addProductsMutation, showWriteError]);

  const removeAllFromSpecificCart = useCallback((cartId: number, productIds: number[]) => {
    productIds.forEach((productId) => {
      removeProductMutation.mutate({ cartId, productId }, {
        onError: (err) => showWriteError(err, 'Could not remove product from cart'),
      });
    });
  }, [removeProductMutation, showWriteError]);

  return {
    cart, carts, activeCartId: resolvedActiveCartId,
    switchCart, createCart, archiveCart, deleteCart, renameCart,
    addToCart, addAllToCart, removeFromCart, clearCart, isInCart,
    addToSpecificCart, removeFromSpecificCart, addAllToSpecificCart, removeAllFromSpecificCart,
  };
}
